<template>
  <b-modal
    id="tenant-invoice-attachments-modal"
    :visible="visible"
    :title="`Attachments — Invoice ${invoice && invoice.id || '...'}`"
    size="xl"
    centered
    hide-footer
    no-close-on-backdrop
    @close="handleClose"
  >
    <div class="attachments-modal-body">
      <div v-if="loadingAttachments" class="text-center py-5">
        <b-spinner class="primary" />
        <p class="mt-2 text-muted">Loading attachments...</p>
      </div>

      <div v-else-if="attachments.length === 0" class="text-center py-5 text-muted">
        <i class="fas fa-paperclip fa-3x mb-3 d-block opacity-50"></i>
        <p>No attachments found for this invoice.</p>
      </div>

      <div v-else class="attachments-list">
        <div
          v-for="attachment in attachments"
          :key="attachment.id"
          class="attachment-item"
          :class="{ downloading: isDownloading(attachment.id) }"
          @click="handleDownload(attachment)"
        >
          <div class="attachment-icon">
            <i class="fas fa-file-alt"></i>
          </div>
          <div class="attachment-info">
            <div class="attachment-name">{{ attachment.name }}</div>
            <div class="attachment-meta uppercase">
              {{ formatFileSize(attachment.size || 0) }}
            </div>
          </div>
          <div class="attachment-action">
            <b-spinner class="primary" v-if="isDownloading(attachment.id)" small />
            <i v-else class="fas fa-download"></i>
          </div>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'

export default {
  name: 'TenantInvoiceAttachmentsModal',
  props: {
    visible: Boolean,
    invoice: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      attachments: [],
      loadingAttachments: false,
      downloading: {}
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.invoice?.id) {
        this.loadAttachments()
      } else {
        this.resetModal()
      }
    }
  },
  methods: {
    ...mapActions('tenantBilling', ['fetchInvoiceAttachments', 'downloadAttachment']),

    async loadAttachments() {
      this.loadingAttachments = true
      this.attachments = []
      try {
        this.attachments = await this.fetchInvoiceAttachments({ invoiceId: this.invoice.id }) || []
      } catch (error) {
        this.$toasted.error(error.message || 'Failed to load attachments', {
          icon: 'exclamation-triangle',
          theme: 'outline',
          position: 'bottom-center',
          duration: 2500
        })
      } finally {
        this.loadingAttachments = false
      }
    },

    isDownloading(id) {
      return !!this.downloading[id]
    },

    async handleDownload(attachment) {
      const id = attachment.id || attachment.name
      if (this.isDownloading(id)) return
      this.$set(this.downloading, id, true)

      try {
        await this.downloadAttachment({ attachmentId: id })
      } catch (error) {
        this.$toasted.error(error.message || `Failed to download ${attachment.name}`, {
          icon: 'exclamation-triangle',
          theme: 'outline',
          position: 'bottom-center',
          duration: 3000
        })
      } finally {
        this.$delete(this.downloading, id)
      }
    },

    handleClose() {
      this.$emit('close')
      setTimeout(() => { this.resetModal()}, 300);
    },

    resetModal() {
      this.attachments = []
      this.loadingAttachments = false
      this.downloading = {}   
    },

    formatFileSize(bytes = 0) {
      if (bytes === 0) return '0 KB'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(i <= 1 ? 0 : 1)) + ' ' + sizes[i]
    }
  }
}
</script>

<style scoped>
.attachments-modal-body {
  min-height: 180px;
  max-height: 50vh;
  overflow-y: auto;
  padding: 0;
}

.attachments-list {
  display: flex;
  flex-direction: column;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.85rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.16s ease;
  background: white;
  margin: .2rem .5rem;
}

.attachment-item:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
  box-shadow: 0 1px 3px rgba(0,0,0,0.05);
}

.attachment-item.downloading {
  opacity: 0.75;
  pointer-events: none;
  background: #f1f5f9;
}

.attachment-icon {
  font-size: 1.75rem;
  color: #64748b;
  flex-shrink: 0;
}

.attachment-info {
  flex: 1;
  min-width: 0;
}

.attachment-name {
  font-weight: 500;
  font-size: 0.96rem;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.attachment-meta.uppercase {
  font-size: 0.8rem;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.attachment-action {
  font-size: 1.25rem;
  color: #475569;
  flex-shrink: 0;
  transition: color 0.2s;
}

.attachment-item:hover .attachment-action, .primary,
.attachment-item.downloading .attachment-action
{
  color: var(--primary-color);
}
</style>